import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { Button } from '.';
import { cn } from '../utils';

export interface PureInputProps {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  withSubmitButton?: boolean;
  fullWidth?: boolean;
  submitButtonDisabled?: boolean;
  variant?: 'base' | 'empty';
  disableAutocomplete?: boolean;
  endAdoramentProps?: React.HTMLAttributes<HTMLDivElement>;
}
export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    PureInputProps {}

export const containerVariants = cva(
  'flex w-full items-center text-sm px-3 py-2 transition-colors duration-200 h-10 ',
  {
    variants: {
      variant: {
        base: cn(
          'rounded-sm border-[1.5px] border-input-border borders text-text-muted hover:border-primary',
          'focus-within:border-primary focus-within:outline-border-primary'
        ),
        empty: '',
      },
    },
  }
);

export const inputVariants = cva(
  'w-full bg-inherit disabled:cursor-not-allowed focus-visible:outline-hidden text-primary transition-colors duration-200',
  {
    variants: {
      variant: {
        base: 'placeholder:text-muted-foreground disabled:opacity-50',
        empty: '',
      },
    },
  }
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      startAdornment,
      endAdornment,
      inputProps,
      withSubmitButton,
      fullWidth,
      submitButtonDisabled,
      variant = 'base',
      disableAutocomplete,
      endAdoramentProps,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn({
          'flex flex-row items-center gap-2': withSubmitButton,
          'w-full': fullWidth,
        })}
      >
        <div className={cn(containerVariants({ variant }), className)}>
          {startAdornment && (
            <div className="text-primary mr-1 cursor-pointer">
              {startAdornment}
            </div>
          )}

          <input
            {...props}
            {...inputProps}
            className={cn(inputVariants({ variant }), inputProps?.className)}
            ref={ref}
            {...(disableAutocomplete && {
              autoComplete: 'off',
            })}
          />

          {endAdornment && (
            <div
              {...endAdoramentProps}
              className={cn(
                'text-primary mx-1 cursor-pointer',
                endAdoramentProps?.className
              )}
            >
              {endAdornment}
            </div>
          )}
        </div>
        {withSubmitButton && (
          <Button disabled={props.disabled || submitButtonDisabled} size="icon">
            <ArrowRight />
          </Button>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
